// src/hooks/useDocumentContext.ts
// Hook for resolving document context (policy, numbering, templates) for a scope entity.

import { useState, useEffect } from 'react';
import { useDocument } from '@react/hooks';

export interface DocumentContext {
  /** Resolved effective policy for the scope */
  effectivePolicy: any | null;
  /** Allowed document type IDs */
  allowedTypeIds: string[];
  /** Default document type ID */
  defaultTypeId: string;
  /** Numbering scheme info */
  numberingScheme: any | null;
  /** Whether approval is required */
  requireApproval: boolean;
  /** Default confidentiality level */
  defaultConfidentiality: string;
}

interface UseDocumentContextResult {
  context: DocumentContext | null;
  isLoading: boolean;
  error: unknown;
}

export function useDocumentContext(
  entityType?: string,
  entityId?: string,
): UseDocumentContextResult {
  const store = useDocument();
  const [context, setContext] = useState<DocumentContext | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!entityType || !entityId) {
      setContext(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await store.ResolveDocumentContext({
          scope_entity: { id: entityId } as any,
          scope_chain: [],
        } as any);
        if (cancelled) return;

        if (result) {
          const policy = result.effective_policy;
          setContext({
            effectivePolicy: policy,
            allowedTypeIds: (policy as any)?.allowed_document_type_ids || [],
            defaultTypeId: (policy as any)?.default_document_type_id || '',
            numberingScheme: result.available_numbering_schemes?.[0] || null,
            requireApproval: !!(policy as any)?.require_approval,
            defaultConfidentiality: (policy as any)?.default_confidentiality || '',
          });
        } else {
          setContext(null);
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [entityType, entityId, store]);

  return { context, isLoading, error };
}
